import { useState } from 'react'
import { Notice } from '../Toast'
import { Link, useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createCampaign, getCampaigns } from '../api/endpoints'
import { ApiError } from '../api/client'
import { ErrorNotice } from '../components'
import { linkState } from '../lib/format'
import Drawer from '../Drawer'
import HowItWorks from '../HowItWorks'

export default function CampaignsPage() {
  const list = useQuery({ queryKey: ['campaigns'], queryFn: getCampaigns })
  const [creating, setCreating] = useState(false)
  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>Campaigns</h1>
        <button type="button" className="primary" onClick={() => setCreating(true)}>New campaign</button>
      </div>
      <p className="muted">A campaign is an admission drive with its own registration link, its own questions, and its own results. Open the link to let people register themselves.</p>
      <HowItWorks id="campaigns" title="How campaigns work" steps={[{ icon: 'campaigns', title: 'Create', text: 'Name it, or copy an earlier campaign with its questions.' }, { icon: 'edit', title: 'Ask what you need', text: 'Add questions such as school, grade or parent details.' }, { icon: 'schedule', title: 'Set dates and a limit', text: 'The link stops working at the closing date. Always set one.' }, { icon: 'results', title: 'Share and watch', text: 'Send the link and follow registrations, tests and results.' }]} />
      <ErrorNotice error={list.error} />
      <div className="table-wrap"><table>
        <thead><tr><th>Campaign</th><th>Registration link</th><th className="num">Registered</th><th className="num">Questions</th></tr></thead>
        <tbody>
          {list.data?.map((c) => {
            const s = linkState(c)
            return (
              <tr key={c.id}>
                <td><Link to={`/campaigns/${c.id}`}><b>{c.title}</b></Link><div className="muted">{c.name}</div></td>
                <td><span className={`badge ${s.state === 'OPEN' ? 'ok' : s.state === 'OFF' ? '' : 'warn'}`}>{s.state === 'OPEN' ? 'Open' : s.state === 'OFF' ? 'Off' : s.state === 'SCHEDULED' ? 'Scheduled' : s.state === 'FULL' ? 'Full' : 'Expired'}</span>
                  <div className="muted" style={{ whiteSpace: 'normal', maxWidth: 380 }}>{s.message}</div></td>
                <td className="num">{c.registeredCount}{c.registrationLimit ? ` / ${c.registrationLimit}` : ''}</td><td className="num">{c.fieldCount}</td>
              </tr>
            )
          })}
        </tbody>
      </table></div>
      {creating && <NewCampaignDrawer campaigns={list.data ?? []} onClose={() => setCreating(false)} />}
    </div>
  )
}

function NewCampaignDrawer({ campaigns, onClose }: { campaigns: { id: number; title: string }[]; onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [copyFrom, setCopyFrom] = useState('')
  const [description, setDescription] = useState('')
  const create = useMutation({
    mutationFn: () => createCampaign({ name: name.trim(), year: new Date().getFullYear(), description: description.trim() || undefined, copyFromCycleId: copyFrom ? Number(copyFrom) : undefined }),
    onSuccess: async (r) => { await queryClient.invalidateQueries({ queryKey: ['campaigns'] }); onClose(); navigate(`/campaigns/${r.id}`) },
  })
  const fields = create.error instanceof ApiError ? create.error.fields : {}
  return (
    <Drawer title="New campaign" width={480} onClose={onClose} onSubmit={() => { if (name.trim()) create.mutate() }}
      footer={<><button type="button" onClick={onClose}>Cancel</button><button type="submit" className="primary" disabled={create.isPending || !name.trim()}>{create.isPending ? 'Creating…' : 'Create campaign'}</button></>}>
      <label className="field">Name<input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Scholarship Test 2027" /></label>
      <label className="field">Start from
        <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
          <option value="">A fresh campaign with the standard questions</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>Copy of {c.title} (its questions and assessments)</option>)}
        </select>
      </label>
      <label className="field">Description (optional)
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} rows={4} placeholder="Internal notes, e.g. source, owner, purpose" />
        <span className="muted" style={{ fontWeight: 400 }}>For staff only. Candidates never see it.</span></label>
      <p className="muted">Registration starts switched off. You can change the questions, dates and link before opening it.</p>
      {Object.values(fields).map((m) => <Notice key={m} tone="error">{m}</Notice>)}
      <ErrorNotice error={Object.keys(fields).length === 0 ? create.error : null} />
    </Drawer>
  )
}
